import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { API_BASE_URL } from '../config/api';
import { useProfile } from "../contexts/ProfileContext";
import axiosClient from "../api/axiosClient";
import Alert from "./Alert";
import { useLanguage } from "../contexts/LanguageContext";

const ToolsTrends = () => {
	const L = useLanguage();
	const [generating, setGenerating] = useState(false);
	const [analysis, setAnalysis] = useState(null);
	const [postsAnalyzed, setPostsAnalyzed] = useState(0);
	const [timeRange, setTimeRange] = useState('month');
	const [customRequest, setCustomRequest] = useState("");
	const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");

	const profile = useProfile();

	const analyzeTrends = async () => {
		try {
			const formData = {};
			formData.age = profile.ageRange;
			formData.gender = profile.genderTarget;
			formData.country = "USA";
			formData.niche = profile.interests;
			formData.timeRange = timeRange;
			formData.customRequest = customRequest;

			setGenerating(true);
			setAnalysis(null);
			setPostsAnalyzed(0);

			const counterInterval = setInterval(() => {
				const volatility = Math.random();
				const increment = volatility > 0.8
					? Math.floor(Math.random() * 5000 + 2000)
					: Math.floor(Math.random() * 300 + 100);

				setPostsAnalyzed(prev => prev + increment);
			}, Math.floor(Math.random() * 200 + 50));

			const response = await axiosClient.post("https://api.viralgpt.com/tools/trends", formData, {
				headers: {
					"Content-Type": "application/json",
				},
			});

			clearInterval(counterInterval);

			if (response.data && response.data.analysis) {
				const trendData = typeof response.data.analysis === 'string'
					? JSON.parse(response.data.analysis)
					: response.data.analysis;

				if (!trendData.trends || !trendData.audio || !trendData.engagement ||
					!trendData.themes || !trendData.technical || !trendData.hooks) {
					throw new Error(L.toolsTrends.invalidData);
				}

				setAnalysis(trendData);
				setFunctionSuccess(L.toolsTrends.success);
			} else {
				throw new Error(L.toolsTrends.invalidResponse);
			}

		} catch (error) {
			console.error("Trend analysis error:", error);
			const errorMessage = error.response?.data?.error ||
				error.response?.data?.details ||
				error.message ||
				L.toolsTrends.unexpectedError;
			setFunctionError(L.toolsTrends.analyzeError(errorMessage));
		} finally {
			setGenerating(false);
			setPostsAnalyzed(0);
		}
	};

	return (
		<div className="bg-white border p-6">
			<h5>{L.toolsTrends.heading}</h5>
			<p className="text-muted">{L.toolsTrends.subtitle}</p>

			<Alert
				variant="danger"
				message={functionError}
				show={!!functionError}
				onClose={() => setFunctionError("")}
			/>

			<Alert
				variant="success"
				message={functionSuccess}
				show={!!functionSuccess}
				onClose={() => setFunctionSuccess("")}
			/>

			<Form.Group controlId="timeRange" className="mb-3">
				<Form.Select
					value={timeRange}
					onChange={(e) => setTimeRange(e.target.value)}
					className="mb-3"
				>
					<option value="week">{L.toolsTrends.ranges.week}</option>
					<option value="month">{L.toolsTrends.ranges.month}</option>
					<option value="year">{L.toolsTrends.ranges.year}</option>
				</Form.Select>
			</Form.Group>

			<Form.Group controlId="customRequest" className="mb-3">
				<Form.Label>{L.toolsTrends.customFocusLabel}</Form.Label>
				<Form.Control
					type="text"
					placeholder={L.toolsTrends.customFocusPlaceholder}
					value={customRequest}
					onChange={(e) => setCustomRequest(e.target.value)}
				/>
			</Form.Group>

			<div className="d-flex align-items-center">
				<Button
					className={generating ? "disabled bg-instagram" : "bg-instagram"}
					onClick={analyzeTrends}
					disabled={generating}
				>
					{generating
						? L.toolsTrends.analyzing(postsAnalyzed.toLocaleString())
						: L.toolsTrends.analyzeButton}
				</Button>
				<div
					style={{ fontSize: "1.5rem" }}
					className={`ms-2 fa fa-circle-notch fa-spin text-instagram ${!generating ? "d-none" : ""}`}
				/>
			</div>

		{analysis && (
			<div className="mt-4">
				{Object.entries({
					[L.toolsTrends.sections.trends]: analysis.trends,
					[L.toolsTrends.sections.hooks]: analysis.hooks,
					[L.toolsTrends.sections.audio]: analysis.audio,
					[L.toolsTrends.sections.engagement]: analysis.engagement,
					[L.toolsTrends.sections.themes]: analysis.themes,
					[L.toolsTrends.sections.technical]: analysis.technical
				}).map(([title, items], index) => (
					<div key={index} className="mb-4">
						<h6 className="text-instagram mb-2">{title}</h6>
						<ul className="list-unstyled m-0">
							{items.map((item, itemIndex) => {
								const text = typeof item === 'string' ? item : item.text;
								const source = typeof item === 'object' ? item.source : null;
								let hostname = '';
								if (source) {
									try { hostname = new URL(source).hostname.replace('www.', ''); } catch { hostname = ''; }
								}
								return (
									<li key={itemIndex} className="d-flex align-items-start mb-2">
										<i className="fa fa-chart-line text-instagram mt-1 me-3" />
										<div className="flex-grow-1">
											<span>{text}</span>
											{source && hostname && (
												<a
													href={source}
													target="_blank"
													rel="noopener noreferrer"
													className="ms-2 text-decoration-none"
													style={{ fontSize: '0.78rem', color: '#635bff', whiteSpace: 'nowrap' }}
												>
													<i className="fa fa-external-link-alt me-1" style={{ fontSize: '0.65rem' }} />
													{hostname}
												</a>
											)}
										</div>
									</li>
								);
							})}
						</ul>
					</div>
				))}
			</div>
		)}
		</div>
	);
};

export default ToolsTrends;